import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Star, MapPin, Filter, Grid, List, Sparkles, Bot, Search, ChevronDown } from 'lucide-react';
import { Vendor } from '../../../types/vendor.ts';
import StoryCard from './StoryCard.tsx';
import VendorShowcase from './VendorShowcase.tsx';

interface SearchResultsPageProps {
  // Optional props for when called programmatically with results
  searchResults?: Vendor[];
  searchQuery?: string;
  aiResponse?: string;
}

interface AISearchResult {
  vendors: Vendor[];
  query: string;
  aiResponse?: string;
  totalResults: number;
}

const SearchResultsPage: React.FC<SearchResultsPageProps> = ({
  searchResults: propSearchResults,
  searchQuery: propSearchQuery,
  aiResponse: propAiResponse
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchResults, setSearchResults] = useState<Vendor[]>(propSearchResults || []);
  const [searchQuery, setSearchQuery] = useState(propSearchQuery || searchParams.get('q') || '');
  const [aiResponse, setAiResponse] = useState(propAiResponse || '');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'relevance' | 'rating' | 'price'>('relevance');
  const [filterBy, setFilterBy] = useState<'all' | 'verified' | 'top'>('all');
  const [isLoading, setIsLoading] = useState(false);

  // Check if we have AI search results from route state or session storage
  useEffect(() => {
    // First check route state
    const state = (window.history.state?.usr as any);
    if (state?.aiSearchResults) {
      const { vendors, query, aiResponse, totalResults } = state.aiSearchResults as AISearchResult;
      setSearchResults(vendors);
      setSearchQuery(query);
      setAiResponse(aiResponse || '');
      return;
    }
    
    // Then check session storage (set by chat store)
    const sessionData = sessionStorage.getItem('aiSearchResults');
    if (sessionData) {
      try {
        const { vendors, query, aiResponse, totalResults } = JSON.parse(sessionData) as AISearchResult;
        setSearchResults(vendors);
        setSearchQuery(query);
        setAiResponse(aiResponse || '');
        // Clear session storage after use
        sessionStorage.removeItem('aiSearchResults');
      } catch (error) {
        console.error('Error parsing session search results:', error);
      }
    }
  }, []);

  // Filter and sort results
  const filteredAndSortedResults = React.useMemo(() => {
    let filtered = [...searchResults];

    // Apply filters
    switch (filterBy) {
      case 'verified':
        filtered = filtered.filter(vendor => vendor.verified);
        break;
      case 'top':
        filtered = filtered.filter(vendor => vendor.topVendor);
        break;
      default:
        break;
    }

    // Apply sorting
    switch (sortBy) {
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'price':
        filtered.sort((a, b) => {
          const aPrice = a.approximateCharge || 0;
          const bPrice = b.approximateCharge || 0;
          return aPrice - bPrice;
        });
        break;
      default: // relevance
        // Keep original order which should be relevance-based
        break;
    }

    return filtered;
  }, [searchResults, filterBy, sortBy]);

  const handleVendorSelect = (vendor: Vendor) => {
    const vendorNameForUrl = vendor.name.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');
    navigate(`/connect/vendor/${vendor.id}/${vendorNameForUrl}`);
  };

  const handleNewSearch = (query: string) => {
    if (query.trim()) {
      setSearchQuery(query);
      // Navigate with new search query
      navigate(`/connect/search?q=${encodeURIComponent(query)}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50/80 via-pink-50/60 to-orange-50/80 pt-20 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 right-10 w-32 h-32 bg-gradient-to-br from-pink-200/20 to-rose-300/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-40 h-40 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-gradient-to-br from-rose-100/10 to-pink-100/10 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Elegant Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/connect')}
              className="p-3 rounded-2xl bg-white/80 backdrop-blur-sm hover:bg-white/90 border border-rose-200/30 transition-all duration-200 shadow-lg hover:shadow-xl group"
            >
              <ArrowLeft className="w-5 h-5 text-rose-600 group-hover:text-rose-700 transition-colors" />
            </motion.button>
            <div className="flex-1">
              <h1 className="text-3xl md:text-4xl font-light text-gray-800 mb-2">
                Your Dream Vendors
              </h1>
              <p className="text-rose-600/80 text-sm font-medium tracking-wide uppercase">
                Curated with Love
              </p>
            </div>
          </div>

          {/* Elegant Search Bar */}
          <div className="relative mb-8">
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-rose-200/20 to-pink-200/20 rounded-2xl blur opacity-50 group-hover:opacity-75 transition-opacity" />
              <div className="relative bg-white/90 backdrop-blur-md rounded-2xl border border-rose-200/40 shadow-lg hover:shadow-xl transition-all duration-300">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-rose-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleNewSearch(searchQuery)}
                  placeholder="Search for your perfect wedding vendors..."
                  className="w-full pl-14 pr-6 py-4 rounded-2xl bg-transparent focus:outline-none text-gray-700 placeholder-rose-400/60 text-lg font-light"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="flex items-center gap-2 text-rose-400/60 text-sm">
                    <span>Press Enter</span>
                    <div className="px-2 py-1 bg-rose-100/60 rounded-md text-xs">⏎</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Elegant AI Response Section */}
          {aiResponse && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-rose-200/20 via-pink-200/20 to-orange-200/20 rounded-2xl blur opacity-40" />
                <div className="relative bg-white/95 backdrop-blur-md rounded-2xl border border-rose-200/30 shadow-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-rose-100 to-pink-100 border border-rose-200/30">
                      <Sparkles className="w-6 h-6 text-rose-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <h3 className="font-semibold text-gray-800 text-lg">Celine's Recommendations</h3>
                        <div className="px-3 py-1 bg-rose-100/80 rounded-full border border-rose-200/50">
                          <span className="text-rose-700 text-xs font-medium">AI Curated</span>
                        </div>
                      </div>
                      <p className="text-gray-700 text-sm leading-relaxed font-light">
                        {aiResponse}
                      </p>
                      <div className="mt-4 pt-4 border-t border-rose-200/30">
                        <div className="flex items-center gap-2 text-rose-600/70 text-xs">
                          <Bot className="w-4 h-4" />
                          <span>Powered by Wedify AI • Personalized for your wedding</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Refined Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 mb-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-gray-700">
              <span className="text-2xl font-light text-rose-600">{filteredAndSortedResults.length}</span>
              <span className="text-sm font-medium">perfect matches</span>
            </div>
            {searchQuery && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>for</span>
                <div className="px-3 py-1 bg-gradient-to-r from-rose-100 to-pink-100 text-rose-700 rounded-full font-medium border border-rose-200/50">
                  "{searchQuery}"
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Elegant Sort Dropdown */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="appearance-none px-4 py-2 pr-8 rounded-xl bg-white/90 backdrop-blur-sm border border-rose-200/40 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-rose-300/30 focus:border-rose-300 cursor-pointer hover:bg-white transition-all"
              >
                <option value="relevance">Best Match</option>
                <option value="rating">Highest Rated</option>
                <option value="price">Budget Friendly</option>
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" />
            </div>

            {/* Elegant Filter Dropdown */}
            <div className="relative">
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value as any)}
                className="appearance-none px-4 py-2 pr-8 rounded-xl bg-white/90 backdrop-blur-sm border border-rose-200/40 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-rose-300/30 focus:border-rose-300 cursor-pointer hover:bg-white transition-all"
              >
                <option value="all">All Vendors</option>
                <option value="verified">Verified ✓</option>
                <option value="top">Premium</option>
              </select>
              <Filter className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-400 pointer-events-none" />
            </div>

            {/* Elegant View Mode Toggle */}
            <div className="flex rounded-xl bg-white/90 backdrop-blur-sm border border-rose-200/40 p-1 shadow-sm">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'grid' 
                    ? 'bg-gradient-to-r from-rose-400 to-pink-400 text-white shadow-sm' 
                    : 'text-rose-600 hover:text-rose-700 hover:bg-rose-50'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-all duration-200 ${
                  viewMode === 'list' 
                    ? 'bg-gradient-to-r from-rose-400 to-pink-400 text-white shadow-sm' 
                    : 'text-rose-600 hover:text-rose-700 hover:bg-rose-50'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex justify-center items-center py-16"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-rose-200/20 to-pink-200/20 rounded-2xl blur opacity-50" />
                <div className="relative bg-white/90 backdrop-blur-md rounded-2xl p-8 border border-rose-200/30 shadow-lg">
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative">
                      <div className="w-12 h-12 border-3 border-rose-300/30 border-t-rose-400 rounded-full animate-spin"></div>
                      <div className="absolute inset-0 w-12 h-12 border-3 border-pink-300/20 border-b-pink-400 rounded-full animate-spin animate-reverse"></div>
                    </div>
                    <div className="text-center">
                      <p className="text-rose-600 font-medium mb-1">Finding Your Perfect Vendors</p>
                      <p className="text-gray-600 text-sm font-light">Curating the best matches for your special day...</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : filteredAndSortedResults.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={
                viewMode === 'grid'
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                  : 'grid grid-cols-1 md:grid-cols-2 gap-8'
              }
            >
              {filteredAndSortedResults.map((vendor, index) => (
                <motion.div
                  key={vendor.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    delay: index * 0.05,
                    type: "spring",
                    stiffness: 100,
                    damping: 15
                  }}
                  className="group"
                >
                  <div className="relative">
                    {/* Wedding-themed decorative element for featured vendors */}
                    {vendor.verified && (
                      <div className="absolute -top-2 -right-2 z-10">
                        <div className="w-8 h-8 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                          <Star className="w-4 h-4 text-white fill-white" />
                        </div>
                      </div>
                    )}
                    <StoryCard
                      vendor={vendor}
                      index={index}
                      featured={false}
                      uniformSize={true}
                      onSelect={() => handleVendorSelect(vendor)}
                    />
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center py-16"
            >
              <div className="relative group max-w-lg mx-auto">
                <div className="absolute inset-0 bg-gradient-to-r from-rose-200/20 to-pink-200/20 rounded-3xl blur opacity-40" />
                <div className="relative bg-white/90 backdrop-blur-md rounded-3xl p-12 border border-rose-200/30 shadow-lg">
                  <div className="w-16 h-16 bg-gradient-to-br from-rose-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6 border border-rose-200/30">
                    <Search className="w-8 h-8 text-rose-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">No Perfect Matches Yet</h3>
                  <p className="text-gray-600 mb-6 font-light leading-relaxed">
                    Don't worry! Your dream vendors are waiting to be discovered. Let's explore more options together.
                  </p>
                  <div className="space-y-3">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate('/connect')}
                      className="w-full px-6 py-3 bg-gradient-to-r from-rose-400 to-pink-400 text-white rounded-xl hover:shadow-lg transition-all duration-200 font-medium"
                    >
                      Explore All Vendors
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSearchQuery('')}
                      className="w-full px-6 py-3 bg-white/80 text-rose-600 rounded-xl border border-rose-200/50 hover:bg-white hover:shadow-md transition-all duration-200 font-medium"
                    >
                      Clear Search & Start Over
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SearchResultsPage; 